import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaDollarSign, FaSave, FaUndo, FaList, FaMoneyBillWave } from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import axios from 'axios';
import './CarWashSpending.css';

const CarWashSpending = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    category: 'largo',
    reason: '',
    comment: '',
    payment_method: 'cash',
    bank_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBankSelect, setShowBankSelect] = useState(false);
  const [recentSpendings, setRecentSpendings] = useState([]);

  const categories = [
    { value: 'largo', label: 'Largo' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'other', label: 'Other' }
  ];

  const banks = [
    { value: 'Awash', label: 'Awash' },
    { value: 'CBE', label: 'CBE' },
    { value: 'Dashen', label: 'Dashen' },
    { value: 'Abyssinia', label: 'Abyssinia' },
    { value: 'Other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'payment_method') {
      setShowBankSelect(value === 'bank');
      if (value !== 'bank') {
        setFormData(prev => ({
          ...prev,
          bank_name: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.reason.trim()) {
      toast.error('Please enter a reason for the spending');
      return;
    }
    
    if (formData.payment_method === 'bank' && !formData.bank_name) {
      toast.error('Please select a bank');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {
          ...formData,
          amount: parseFloat(formData.amount),
          action: 'add_car_spending'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success('Spending recorded successfully!');
        // Reset form
        setFormData({
          amount: '',
          category: 'largo',
          reason: '',
          comment: '',
          payment_method: 'cash',
          bank_name: ''
        });
        setShowBankSelect(false);
        // Refresh recent spendings
        fetchRecentSpendings();
      } else {
        throw new Error(response.data.message || 'Failed to record spending');
      }
    } catch (error) {
      console.error('Error recording spending:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while recording spending';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentSpendings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINT, {
        params: { action: 'get_recent_car_spendings' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRecentSpendings(response.data.spendings || []);
      }
    } catch (error) {
      console.error('Error fetching recent spendings:', error);
    }
  };

  useEffect(() => {
    fetchRecentSpendings();
  }, []);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString('en-US', options);
  };

  return (
    <div className="carwash-spending">
      <div className="page-header">
        <h1><FaMoneyBillWave className="header-icon" /> Car Wash Spending</h1>
        <p>Record and manage car wash related expenses</p>
      </div>

      <div className="spending-container">
        <div className="spending-form">
          <div className="card">
            <h2><FaDollarSign /> Record New Spending</h2>
            
            {error && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Amount (ETB) *</label>
                <div className="input-with-icon">
                  <FaDollarSign className="input-icon" />
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Reason *</label>
                <input
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Enter reason for spending"
                  className="form-control"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
              
              {showBankSelect && (
                <div className="form-group">
                  <label>Bank Name *</label>
                  <select
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className="form-control"
                    required={formData.payment_method === 'bank'}
                  >
                    <option value="">Select Bank</option>
                    {banks.map(bank => (
                      <option key={bank.value} value={bank.value}>
                        {bank.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="form-group">
                <label>Comments (Optional)</label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  placeholder="Additional notes or details"
                  className="form-control"
                  rows="3"
                />
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setFormData({
                      amount: '',
                      category: 'largo',
                      reason: '',
                      comment: '',
                      payment_method: 'cash',
                      bank_name: ''
                    });
                    setShowBankSelect(false);
                    setError('');
                  }}
                  disabled={loading}
                >
                  <FaUndo /> Reset
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : <><FaSave /> Save Spending</>}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <div className="recent-spendings">
          <div className="card">
            <h2><FaList /> Recent Spendings</h2>
            
            {recentSpendings.length === 0 ? (
              <div className="empty-state">
                <p>No recent spendings found</p>
              </div>
            ) : (
              <div className="spending-list">
                {recentSpendings.map(spending => (
                  <div key={spending.id} className="spending-item">
                    <div className="spending-amount">
                      {parseFloat(spending.amount).toFixed(2)} ETB
                    </div>
                    <div className="spending-details">
                      <div className="spending-category">
                        {spending.category.charAt(0).toUpperCase() + spending.category.slice(1)}
                      </div>
                      <div className="spending-reason">{spending.reason}</div>
                      <div className="spending-meta">
                        <span className="spending-date">
                          {formatDate(spending.created_at)}
                        </span>
                        <span className="spending-payment">
                          {spending.payment_method === 'bank' 
                            ? `Bank: ${spending.bank_name}` 
                            : 'Cash'}
                        </span>
                      </div>
                      {spending.comment && (
                        <div className="spending-comment">
                          <strong>Note:</strong> {spending.comment}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarWashSpending;
